<script setup lang="ts">

import {faCheck} from "@fortawesome/pro-regular-svg-icons";
import {FormCheckbox, FormDatePicker, FormInput, FormTextarea} from "@/shared/components/ui/form2/element";
import {Form} from "@/shared/components/ui/form2/";
import PaymentMethodList from "@/shared/components/billing/PaymentMethodList.vue";
import Loader from "@/shared/components/ui/loader/Loader.vue";
import AddressList from "@/shared/components/address/AddressList.vue";
import SubmitButton from "@/shared/components/SubmitButton.vue";
import {Address, AddressInput, type CheckoutDeliveryMethod, DeliveryMethod, ObjectFieldLayout, PaymentMethod} from "@/shared/graphql/types";
import {computed, ref, watch} from "vue";
import {useCheckoutStore} from "@/checkout/stores/checkout.store.ts";
import {useI18n} from "vue-i18n";
import AddressFormInput from "@/shared/components/address/AddressFormInput.vue";
import yup from "@/shared/composable/validationMessages.ts";
import CreateDeliveryAddressForm from "@/shared/components/address/CreateDeliveryAddressForm.vue";
import {useDialog} from "@/shared/components/ui/dialog/use-dialog.ts";
import {addressService} from "@/shared/services/address.service.ts";
import {RadioCard, RadioCardGroup} from "@/shared/components/ui/radio-group";
import {faBuilding, faTruck} from "@fortawesome/pro-solid-svg-icons";
import DeliveryMethodList from "@/shared/components/delivery/DeliveryMethodList.vue";
import {debounce} from "@/shared/composable/utils.ts";
import {Alert, AlertDescription, AlertTitle} from "@/shared/components/ui/alert";
import {objectLayoutForObject} from "@/shared/composable/object.ts";
import {addressToInput, defaultAddressValidationSchema} from "@/shared/components/address";
import {useCheckoutFunctions} from "@/checkout/lib/common.ts";
import {VStack} from "@/shared/components/ui/helper";

type CheckoutForm = {
  remark: string
  deliveryDate: Date
  pickupDate: Date
  customerReference: string
  deliveryAddress: AddressInput
  billingAddress: AddressInput
}
type ClientDeliveryType = 'DELIVERY' | 'PICKUP'

const {t} = useI18n()
const {dialog} = useDialog();
const checkout = useCheckoutStore();
const {cancelCheckout} = useCheckoutFunctions();

const busy = ref<boolean>(false)
const maxDate = new Date();
maxDate.setMonth(maxDate.getMonth() + 1);
const errors = ref()
const layout: ObjectFieldLayout = objectLayoutForObject(checkout.settings.objectLayouts, "Address")
const isHouseNumberSeparate = layout.valueOf() === "ADDRESS_STREET_HOUSE_NR";
const checkoutForm = ref<CheckoutForm>({
  remark: checkout.remark,
  deliveryDate: null,
  pickupDate: null,
  customerReference: checkout.customerReference,
  deliveryAddress: addressToInput(),
  billingAddress: addressToInput()
})
const createAddressYup = () => defaultAddressValidationSchema(isHouseNumberSeparate)
const isBpost = computed(() => checkout.deliveryMethod?.kind == 'BPOST')
const isGuestBasket = computed(() => checkout.isGuestBasket)
const isOnlinePayment = computed(() => checkout.paymentMethod.isOnline ?? true)
const isSalesRepAccount = computed(() => checkout.isSalesRepAccount ?? false)

const validationSchema = computed(() => {
  const baseFields: Record<string, any> = {
    remark: yup.string().notRequired(),
    customerReference: yup.string().notRequired().max(200),
  };
  if (dateSelection.value) {
    if (isDelivery.value) baseFields.deliveryDate = yup.date().required();
    if (isPickup.value) baseFields.pickupDate = yup.date().required();
  }
  if (isGuestBasket.value) {
    if (isDelivery.value) baseFields.deliveryAddress = createAddressYup().required();
    if (billingAddressRequired.value) baseFields.billingAddress = createAddressYup().required();
  }
  return yup.object().shape(baseFields);
});
const dateSelection = computed(() => checkout.deliveryMethod ? (checkout.deliveryMethod["dateSelectionRequired"] == true) : false)
const unavailableDateSet = computed(() => {
  if (!checkout.deliveryMethod) return new Set()
  const unavailableDates = (checkout.deliveryMethod)['unavailableDates'];
  return new Set(unavailableDates.map((value: any) => new Date(value['date']).toDateString()));
})
const earliestDeliveryDate = computed(() => {
  if (!checkout.deliveryMethod) return null
  const dates = (checkout.deliveryMethod)['unavailableDates'];
  if (!dates) return null
  if (dates.length == 0) return null
  return dates[0]
})
const deliveryTypes = computed(() => checkout.deliveryTypes)
const defaultDeliveryType = computed(() => {
  if (!checkout.deliveryMethod) return deliveryTypes.value.length > 0 ? deliveryTypes.value[0] : null
  const deliveryCategory = getDeliveryCategory(checkout.deliveryMethod)
  return deliveryTypes.value.find(type => type == deliveryCategory) || deliveryTypes.value[0]
})
const deliveryMethods = computed(() => checkout.deliveryMethods.filter(method => getDeliveryCategory(method) == "DELIVERY"))
const isDelivery = ref<boolean>(defaultDeliveryType.value == 'DELIVERY');
const isPickup = computed(() => !isDelivery.value)
const billingSameAsDelivery = ref(true)
const billingAddressToggle = computed(() => {
  if (!isGuestBasket.value) return false //If customer is known we don't need the toggle because we know the billing address
  return isDelivery.value //Only for the delivery type do we need the billing address toggle, in case of pickup it is required and does not need a toggle
})
const billingAddressRequired = computed(() => {
  if (!isGuestBasket.value) return false //If not a guest then the billingAddress is never required because we already know it
  if (isPickup.value) return true //Pickup and guest, billing address is required
  return !billingSameAsDelivery.value //In other case only if the toggle indicates that it is different then the delivery address
})

function getDeliveryCategory(method: CheckoutDeliveryMethod): ClientDeliveryType {
  if (method.kind === 'PICKUP') return 'PICKUP'
  return 'DELIVERY'
}

const updateDeliveryType = async (deliveryType: ClientDeliveryType) => {
  isDelivery.value = deliveryType == 'DELIVERY'
  await whileBusy(async () => {
    const deliveryMethod = checkout.deliveryMethods.find(method => getDeliveryCategory(method) == deliveryType) //Select the first method that confirms to this type
    await checkout.updateDeliveryMethod(deliveryMethod)
  })
}
const updateDeliveryMethod = async (deliveryMethod: DeliveryMethod) => {
  await whileBusy(async () => {
    await checkout.updateDeliveryMethod(deliveryMethod)
  })
}
const updateSelectedDeliveryAddress = async (address: Address) => {
  await whileBusy(async () => {
    await checkout.updateDeliveryAddress(address)
  })
}
const updateGuestDeliveryAddress = async () => {
  await whileBusy(async () => {
    if (isGuestBasket.value) {
      const form = checkoutForm.value
      //In case of a guest basket we need to ask for the address in full through the address forms
      checkout.deliveryAddressesInput = form.deliveryAddress
      if (billingAddressRequired.value) {
        checkout.billingAddressInput = form.billingAddress
      }
      if (dateSelection.value) {
        checkout.preferredDeliveryDate = isDelivery.value ? form.deliveryDate : form.pickupDate
      } else {
        checkout.preferredDeliveryDate = null
      }
      await checkout.updateCheckout()
    }
  })
}
const updateGuestDeliveryAddressDebounced = debounce(updateGuestDeliveryAddress, 1000)
const updatePaymentMethod = async (paymentMethod: PaymentMethod) => {
  await whileBusy(async () => {
    await checkout.updatePaymentMethod(paymentMethod)
  })
}
const createDeliveryAddress = async () => {
  try {
    const response = await dialog<Address>({
      title: t('create_address'),
      component: CreateDeliveryAddressForm,
      props: {
        customerId: checkout.customer.id,
        countries: checkout.settings.countries,
        isHouseNumberSeparate: isHouseNumberSeparate,
      },
    })
    if (response.finished) checkout.deliveryAddresses = [...checkout.deliveryAddresses, response.result];
  } catch (e) {
    console.log("Dialog was closed without doing anything")
  }
}
const deleteDeliveryAddress = async (address: Address) => {
  const result = await addressService.deleteAddress(checkout.customer.id, address.id);
  if (result.validation.success) {
    checkout.deliveryAddresses = [...checkout.deliveryAddresses.filter(addr => addr.id !== address.id)];
  }
}
const unavailableDates = (date: Date): boolean => unavailableDateSet.value.has(date.toDateString())
const initDefaultDeliveryDate = () => {
  if (!checkout.deliveryMethod) return
  const date = (checkout.deliveryMethod)["firstAvailableDate"]
  if (date && !checkoutForm.value.deliveryDate && isDelivery.value) {
    checkoutForm.value.deliveryDate = new Date(date)
  }
  if (date && !checkoutForm.value.pickupDate && isPickup.value) {
    checkoutForm.value.pickupDate = new Date(date)
  }
}
const whileBusy = async (work: () => Promise<void>) => {
  busy.value = true
  try {
    await work()
  } catch (e) {
    console.error(e)
  } finally {
    busy.value = false
  }
}
const completeCheckout = async (checkoutForm: CheckoutForm) => {
  await whileBusy(async () => {
    if (isGuestBasket.value) {
      //In case of a guest basket we need to ask for the address in full through the address forms
      checkout.deliveryAddressesInput = checkoutForm.deliveryAddress
      if (billingAddressRequired.value) {
        checkout.billingAddressInput = checkoutForm.billingAddress
      }
    }
    checkout.customerReference = checkoutForm.customerReference
    checkout.remark = checkoutForm.remark
    if (dateSelection.value) {
      checkout.preferredDeliveryDate = isDelivery.value ? checkoutForm.deliveryDate : checkoutForm.pickupDate
    } else {
      checkout.preferredDeliveryDate = null
    }
    await checkout.complete()
    if (checkout.valid) {
      const payment = checkout.payment
      if (payment.nextActionUrl) {
        window.location.href = payment.nextActionUrl
      }
    } else {
      // await alertDialog({
      //   title: t("validation_dialog_title"),
      //   description: t("validation_dialog_general_error"),
      //   confirmLabel: "Ok",
      //   type: AlertDialogType.ERROR,
      // })
    }
  })
}
const calculateMethodsByDeliveryType = (methods: CheckoutDeliveryMethod[]): Map<ClientDeliveryType, CheckoutDeliveryMethod[]> => {
  // Create an initial grouping object
  const grouped: Record<ClientDeliveryType, CheckoutDeliveryMethod[]> = {
    DELIVERY: [],
    PICKUP: []
  };
  methods.forEach(method => {
    // Ensure that the deliveryType is one of the expected ones
    const deliveryType = getDeliveryCategory(method)
    if (deliveryType === 'DELIVERY' || deliveryType === 'PICKUP') {
      grouped[deliveryType].push(method);
    }
  });
  return new Map<ClientDeliveryType, CheckoutDeliveryMethod[]>(
      Object.entries(grouped) as [ClientDeliveryType, CheckoutDeliveryMethod[]][]
  );
}
const methodsByDeliveryType = calculateMethodsByDeliveryType(checkout.deliveryMethods)
const deliveryTypeDescription = (type: ClientDeliveryType): string => {
  let description = type == 'PICKUP' ? t('delivery.pickup_description') : t('delivery.delivery_description')
  const methods = methodsByDeliveryType.get(type)
  if (methods.length == 1) {
    const price = methods[0].cost?.formatted
    //When there is only one deliveryMethod no further choice must be made, and we can show the price of each type in the card
    if (price) description += `<br>${price}`
  }

  return description
}

watch(earliestDeliveryDate, initDefaultDeliveryDate);
let currentLocation = `${checkoutForm.value.deliveryAddress.country}-${checkoutForm.value.deliveryAddress.zipCode}`
watch(checkoutForm, (form: CheckoutForm) => {
  const newLocation = `${checkoutForm.value.deliveryAddress.country}-${checkoutForm.value.deliveryAddress.zipCode}`
  if (newLocation != currentLocation) {
    form.deliveryAddress.zipCode
    form.deliveryAddress.country
    updateGuestDeliveryAddressDebounced() //Server update to recalculate the delivery options
    currentLocation = newLocation
  }
}, {deep: true});
initDefaultDeliveryDate()
</script>

<template>
  <Form @submit="completeCheckout"
        v-if="checkoutForm"
        v-model="checkoutForm"
        :debug="false"
        :columns="1"
        :errors="errors"
        :validation="validationSchema"
        class="px-4 pb-32 pt-8 lg:pt-16 sm:px-6 lg:col-start-1 lg:row-start-1 lg:px-0 lg:pb-16">

    <div class="mx-auto max-w-lg lg:max-w-none w-full">
      <Loader v-if="checkout.loading"/>
      <div v-else class="space-y-12">

        <div v-for="error in checkout.validation.globalErrors" :key="error.message" class="text-red-500 text-sm">
          {{ error.message }}
        </div>

        <section aria-labelledby="shipment-heading" class="border-b border-gray-900/10 pb-6">
          <h2 id="shipment-heading" class="text-lg font-medium text-gray-900">{{ t('shared.checkout.shipping') }}</h2>
          <div v-if="deliveryTypes.length > 1" class="mb-6">
            <p class="my-default text-sm leading-6 text-slate-500">{{ t('shared.checkout.choose_delivery_method') }}</p>

            <RadioCardGroup :col-span="2" :cols="2" @update:model-value="updateDeliveryType" :default-value="defaultDeliveryType">
              <RadioCard v-for="deliveryType in deliveryTypes" :value="deliveryType"
                         :label="deliveryType == 'PICKUP' ? t('delivery.pickup'):t('delivery.delivery')"
                         :icon="deliveryType == 'PICKUP' ? faBuilding:faTruck"
                         :description="deliveryTypeDescription(deliveryType)"/>
            </RadioCardGroup>
          </div>

          <div v-if="isDelivery">
            <p class="my-default text-sm leading-6 text-slate-500">{{ t('shared.checkout.choose_delivery_address') }}</p>
            <div v-if="isGuestBasket">
              <AddressFormInput
                  v-model="checkoutForm.deliveryAddress"
                  v-validation="{path: 'deliveryAddress'}"
                  :countries="checkout.settings?.countries ?? []"
                  :is-house-number-separate="isHouseNumberSeparate"/>
            </div>
            <AddressList v-else
                         :addresses="checkout.deliveryAddresses"
                         :selected="checkout.deliveryAddress?.id"
                         :columns="2"
                         :colspan="1"
                         :delete-action="false"
                         :disabled="busy"
                         @address:selected="updateSelectedDeliveryAddress"
                         @address:create="createDeliveryAddress"
                         @address:delete="deleteDeliveryAddress"
            />

            <!-- If there are multiple delivery methods we need to let the user select one BPOST, own transport, ...            -->
            <div v-if="deliveryMethods.length > 1" class="mb-6">
              <p class="my-default text-sm leading-6 text-slate-500">{{ t('shared.checkout.choose_delivery') }}</p>
              <DeliveryMethodList :delivery-methods="deliveryMethods"
                                  :model-value="checkout.deliveryMethod"
                                  :disabled="busy"
                                  @delivery-method:selected="updateDeliveryMethod"
              />
            </div>
            <Alert v-if="deliveryMethods.length === 0" variant="negative" class="mt-default">
              <AlertTitle>{{ t('delivery.none.title') }}</AlertTitle>
              <AlertDescription>
                <p>{{ t('delivery.none.description') }}</p>
              </AlertDescription>
            </Alert>

            <template v-if="dateSelection">
              <p class="my-default text-sm leading-6 text-slate-500">{{ t('shared.checkout.choose_delivery_date') }}</p>
              <div class="w-full">
                <FormDatePicker
                    v-model="checkoutForm.deliveryDate"
                    v-validation
                    :min-date="new Date()"
                    :max-date="maxDate"
                    :is-date-unavailable="unavailableDates"
                    :label="t('shared.checkout.delivery_date')"
                />
              </div>
            </template>
          </div>
          <template v-else-if="isPickup">
            <p class="my-default text-sm leading-6 text-slate-500">{{ t('shared.checkout.choose_pickup_address') }}</p>
            <AddressList :addresses="checkout.deliveryAddresses"
                         :selected="checkout.deliveryAddress?.id"
                         :columns="2"
                         :colspan="1"
                         :delete-action="false"
                         :create-action="false"
                         :disabled="busy"
                         @address:selected="updateSelectedDeliveryAddress"
            />
            <template v-if="dateSelection">
              <p class="my-default text-sm leading-6 text-slate-500">{{ t('shared.checkout.choose_pickup_date') }}</p>
              <div class="w-full">
                <FormDatePicker
                    v-model="checkoutForm.pickupDate"
                    v-validation
                    :min-date="new Date()"
                    :max-date="maxDate"
                    :is-date-unavailable="unavailableDates"
                    :label="t('shared.checkout.pickup_date')"
                />
              </div>
            </template>
          </template>
          <template v-else-if="isBpost">
            <!--Not supported at the moment-->
          </template>
        </section>

        <section aria-labelledby="payment-heading" class="border-b border-gray-900/10 pb-6">
          <h2 id="payment-heading" class="text-lg font-medium text-gray-900">{{ t('shared.checkout.payment') }}</h2>

          <div v-if="isGuestBasket" class="mb-12">
            <p class="my-default text-sm leading-6 text-slate-500">{{ t('shared.checkout.choose_billing_address') }}</p>
            <FormCheckbox v-if="billingAddressToggle"
                          id="billingSameAsDelivery"
                          v-model="billingSameAsDelivery"
                          :checked="billingSameAsDelivery"
                          :label="t('shared.checkout.same_as_delivery')"/>

            <AddressFormInput v-if="billingAddressRequired"
                              v-model="checkoutForm.billingAddress"
                              v-validation="{path: 'billingAddress'}"
                              :countries="checkout.settings?.countries ??[]"
                              :is-house-number-separate="isHouseNumberSeparate"/>
          </div>


          <div>
            <p class="my-default text-sm leading-6 text-slate-500">{{ t('shared.common.payment_method') }}</p>
            <PaymentMethodList
                :payment-methods="checkout.paymentMethods"
                :selected="checkout.paymentMethod?.code"
                @payment-method:selected="updatePaymentMethod"
            />
          </div>
        </section>

        <section aria-labelledby="shipping-heading" class="mt-10">
          <h2 id="shipping-heading" class="text-lg font-medium text-gray-900 mb-6">
            {{ t('shared.checkout.extra_info') }}
          </h2>
          <VStack >
            <FormInput
                v-model="checkoutForm.customerReference"
                v-validation
                :label="t('shared.common.own_reference')"/>
            <FormTextarea
                v-model="checkoutForm.remark"
                v-validation
                :label="isSalesRepAccount ? t('shared.common.internal_remarks') : t('shared.common.remarks')"
                :rows="3"/>
          </VStack>
        </section>

        <div class="mt-10 border-t border-gray-200 pt-6 text-center">
          <SubmitButton
              class="w-full"
              :disabled="!checkout.valid"
              :icon="faCheck"
              :loading="busy">
            <template v-if="isOnlinePayment">{{ t('shared.common.checkout') }}</template>
            <template v-else>{{ t('shared.common.checkout_confirm') }}</template>
          </SubmitButton>
          <a @click.prevent="cancelCheckout" :href="checkout.config.basketReturn.url"
             class="mt-default block text-slate-500 hover:text-gray-900 text-xs underline"
             v-html="checkout.config.basketReturn.label"/>
        </div>
      </div>
    </div>
  </Form>
</template>

<style scoped>

</style>